// src/components/ProcessorStateBadge.tsx
//
// Compact heartbeat-status badge for a processor card. Replaces the per-card
// `Status: Enabled / Disabled` line which read from one Azure instance's
// APScheduler and was wrong whenever the request landed on a different
// instance from the one running the job.
//
// The badge is backed by Cosmos `processorStates` (single source of truth
// across all 4 instances) via GET /ui/admin/processor-state, polled once
// every 10s for the whole page (one request, not one per badge).

import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui';
import { formatWhen } from './curation/format';

export interface ProcessorStateDoc {
  id: string;
  desiredState: 'running' | 'stopped';
  actualState: 'idle' | 'starting' | 'running' | 'finishing' | 'crashed' | 'stopped';
  ownerInstanceId: string | null;
  ownerBootId: string | null;
  startedAt: string | null;
  startedBy: string | null;
  lastHeartbeatAt: string | null;
  currentPhase: string | null;
  lastRunStats: Record<string, unknown> | null;
  /** Per-RUN. release_lease overwrites this on every clean finish (with
   *  null), so it only ever shows a problem from the run currently in
   *  flight or the one that just failed. */
  lastError: string | null;
  /** STANDING condition (e.g. CAF_SPLIT_CUTOVER unset). Written only by
   *  processor_state_service.note_warning and never cleared by the lease
   *  lifecycle, so unlike lastError it is still here when an operator looks.
   *  Older docs predate the field — treat undefined as null. */
  lastWarning?: string | null;
  lastUpdatedAt: string;
}

const POLL_INTERVAL_MS = 10_000;

/**
 * Fetch all processor state docs and refresh every 10s. Returns a map keyed
 * by processor id so badges can look themselves up.
 */
export function useProcessorStates(): Record<string, ProcessorStateDoc> {
  const { apiRequest } = useAuth();
  const [statesByID, setStatesByID] = useState<Record<string, ProcessorStateDoc>>({});

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const resp = await apiRequest<{ states: ProcessorStateDoc[] }>({
          url: '/ui/admin/processor-state',
          method: 'GET',
        });
        if (cancelled) return;
        const next: Record<string, ProcessorStateDoc> = {};
        for (const s of resp.states || []) {
          next[s.id] = s;
        }
        setStatesByID(next);
      } catch (err) {
        // Soft-fail — leaving the last-known map in place is better than
        // flicker-clearing it on a transient network error.
        console.error('useProcessorStates: fetch failed:', err);
      }
    };

    fetchOnce();
    const handle = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [apiRequest]);

  return statesByID;
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'unknown';
  const diffSecs = Math.round((Date.now() - ts) / 1000);
  if (diffSecs < 0) return 'in future';
  if (diffSecs < 5) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  const mins = Math.floor(diffSecs / 60);
  if (mins < 60) return `${mins}m ${diffSecs % 60}s ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return new Date(iso).toLocaleString();
}

function pillColors(state: ProcessorStateDoc['actualState']): { bg: string; fg: string } {
  switch (state) {
    case 'running':
      return { bg: '#d1fae5', fg: '#065f46' };
    case 'starting':
    case 'finishing':
      return { bg: '#fef3c7', fg: '#92400e' };
    case 'crashed':
      return { bg: '#fee2e2', fg: '#991b1b' };
    case 'idle':
      return { bg: '#dbeafe', fg: '#1e40af' };
    case 'stopped':
    default:
      return { bg: '#e5e7eb', fg: '#374151' };
  }
}

function heartbeatColor(iso: string | null | undefined): string {
  if (!iso) return '#6b7280';
  const ageMs = Date.now() - new Date(iso).getTime();
  if (ageMs > 300_000) return '#b91c1c'; // red after 5 min
  if (ageMs > 60_000) return '#b45309';  // amber after 60s
  return '#065f46';                       // green
}

interface BadgeProps {
  /**
   * The processor state document for this badge. `undefined` means the
   * processor has not claimed a lease yet (e.g. first boot, awaiting first
   * tick) or the parent fetch hasn't returned. We render a neutral
   * "no state yet" pill rather than a misleading "DISABLED".
   */
  state: ProcessorStateDoc | undefined;
}

const ProcessorStateBadge: React.FC<BadgeProps> = ({ state }) => {
  if (!state) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#6b7280',
        }}
      >
        <span
          style={{
            padding: '2px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            background: '#e5e7eb',
            color: '#6b7280',
          }}
        >
          NO STATE YET
        </span>
        <span style={{ fontStyle: 'italic' }}>
          waiting for first heartbeat from any instance
        </span>
      </div>
    );
  }

  const { bg, fg } = pillColors(state.actualState);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        fontSize: '13px',
        color: '#374151',
      }}
    >
      <span
        title={`desired: ${state.desiredState}`}
        style={{
          padding: '2px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 700,
          background: bg,
          color: fg,
        }}
      >
        {state.actualState.toUpperCase()}
      </span>

      <span>
        <strong>Last heartbeat:</strong>{' '}
        <span style={{ color: heartbeatColor(state.lastHeartbeatAt) }}>
          {formatRelativeTime(state.lastHeartbeatAt)}
        </span>
      </span>

      {state.currentPhase && (
        <span style={{ color: '#6b7280' }}>
          phase: <em>{state.currentPhase}</em>
        </span>
      )}

      {state.ownerInstanceId && (
        <span
          title={state.ownerBootId ? `boot ${state.ownerBootId.substring(0, 8)}` : undefined}
          style={{
            fontFamily: 'monospace',
            fontSize: '11px',
            color: '#9ca3af',
          }}
        >
          owner: {state.ownerInstanceId}
        </span>
      )}

      {state.lastError && (
        <span style={{ color: '#b91c1c', fontSize: '12px' }} title={state.lastError}>
          ⚠ last error: {state.lastError.length > 60 ? `${state.lastError.slice(0, 60)}…` : state.lastError}
        </span>
      )}

      {state.lastWarning && (
        <span style={{ color: '#b45309', fontSize: '12px' }} title={state.lastWarning}>
          ⚠ warning: {state.lastWarning.length > 60 ? `${state.lastWarning.slice(0, 60)}…` : state.lastWarning}
        </span>
      )}
    </div>
  );
};

export default ProcessorStateBadge;


// ---------------------------------------------------------------------------
// Full panel — matches the "Consolidated state (multi-instance, Cosmos-backed)"
// block that lived inline in the CAF Inbox card. Reusable across every
// processor with a state_id in `processorStates`. Includes Start/Stop/Clear-
// crashed controls that hit the generic /processor-state/<id>/<action>
// endpoints.
// ---------------------------------------------------------------------------

interface PanelProps {
  processorId: string;
  state: ProcessorStateDoc | undefined;
  /** The CAF Backlog is switched on and off from AI Curation > Automation: no Start/Stop here. */
  readOnly?: boolean;
}

const ACTION_FAILED: Record<'start' | 'stop' | 'clear-crashed', string> = {
  start: "Couldn't start the processor.",
  stop: "Couldn't stop the processor.",
  'clear-crashed': "Couldn't clear the crash.",
};

export const ProcessorStatePanel: React.FC<PanelProps> = ({ processorId, state, readOnly = false }) => {
  const { apiRequest } = useAuth();
  const [actionError, setActionError] = useState<string>('');
  const [actionPending, setActionPending] = useState<boolean>(false);

  const callAction = async (action: 'start' | 'stop' | 'clear-crashed') => {
    setActionError('');
    setActionPending(true);
    try {
      await apiRequest({ url: `/ui/admin/processor-state/${processorId}/${action}`, method: 'POST' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(`${ACTION_FAILED[action]} ${msg}`);
      console.error(`processor-state/${processorId}/${action} failed:`, err);
    } finally {
      setActionPending(false);
    }
  };

  return (
    <div
      className="processor-state-card"
      style={{ marginTop: '12px', padding: '14px', background: '#fafbfc', border: '1px solid #dde1e6', borderRadius: '6px' }}
    >
      <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#374151' }}>Processor status</h4>

      {actionError && (
        <div role="alert" style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>{actionError}</div>
      )}

      {!state && (
        <div style={{ color: '#6b7280', fontSize: '13px' }}>Not started yet on any server.</div>
      )}

      {state && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {(() => {
              const { bg, fg } = pillColors(state.actualState);
              return (
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', backgroundColor: bg, color: fg }}>
                  {state.actualState.toUpperCase()}
                </span>
              );
            })()}
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Asked to be: <strong>{state.desiredState}</strong>
            </span>
          </div>

          <div style={{ marginTop: '10px', fontSize: '13px', color: '#4b5563' }}>
            <div>
              <strong>Last heartbeat:</strong>{' '}
              <span style={{ color: heartbeatColor(state.lastHeartbeatAt) }}>{formatRelativeTime(state.lastHeartbeatAt)}</span>
            </div>
            {state.currentPhase && <div><strong>Phase:</strong> {state.currentPhase}</div>}
            {state.startedAt && (
              <div>
                <strong>Started:</strong> {formatWhen(state.startedAt)}{' '}
                {state.startedBy && <em>by {state.startedBy}</em>}
              </div>
            )}
            {state.lastError && (
              <div style={{ color: '#b91c1c', marginTop: '4px' }}><strong>Last error:</strong> {state.lastError}</div>
            )}
            {state.lastWarning && (
              <div style={{ color: '#b45309', marginTop: '4px' }}><strong>Warning:</strong> {state.lastWarning}</div>
            )}
          </div>

          <details style={{ marginTop: '10px' }}>
            <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#374151' }}>Technical details</summary>
            <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>
              {state.ownerInstanceId && <div>Server: {state.ownerInstanceId}</div>}
              {state.ownerBootId && <div>Boot: {state.ownerBootId.substring(0, 8)}</div>}
            </div>
            {state.lastRunStats && (
              <pre style={{ margin: '6px 0 0 0', padding: '8px', background: '#f3f4f6', borderRadius: '4px', fontSize: '12px', overflowX: 'auto' }}>
                {JSON.stringify(state.lastRunStats, null, 2)}
              </pre>
            )}
          </details>
        </>
      )}

      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {!readOnly && (<>
        <Button size="sm" variant="secondary" onClick={() => callAction('start')}
          disabled={actionPending || state?.desiredState === 'running'}>Start</Button>
        <Button size="sm" variant="ghost" onClick={() => callAction('stop')}
          disabled={actionPending || state?.desiredState === 'stopped'}>Stop</Button>
        </>)}
        <Button size="sm" variant="ghost" onClick={() => callAction('clear-crashed')}
          disabled={actionPending || state?.actualState !== 'crashed'}>Clear crash</Button>
      </div>
    </div>
  );
};
